import useRouter from 'common/hooks/use-router';
import useItem from 'common/hooks/useItem';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AlertsContext from 'common/providers/alerts';
import StudentForm from './StudentForm';
import useList from 'common/hooks/useList';

function Student() {
  const { t } = useTranslation();
  const router = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const id = router.query.id as string;
  const userId = router.query.userId as string;

  const {
    updateItem: updateUser,
    item: user,
    error: userError,
    isFetching,
  }: any = useItem('users', id || userId, {
    populate: ['role', 'profiles', 'profiles.establishment', 'profiles.establishment.department'],
  });

  const { fetchItems: refreshUsers, error: usersError } = useList('users', {
    populate: ['role', 'profiles', 'profiles.establishment', 'profiles.establishment.department'],
  });

  const {
    saveItem: saveProfile,
    updateItem: updateProfile,
    error: profileError,
  }: any = useItem('profiles', null, {
    manual: true,
  });

  useEffect(() => {
    if (userError || usersError || profileError) {
      const e = userError || usersError || profileError;
      setAlert(
        e === 'This attribute must be unique' ? t('common.emailAlreadyTaken') : t('common.error'),
        'danger'
      );
    }
  }, [userError, usersError, profileError, setAlert, t]);

  const back = useCallback(() => {
    if (!userError && !usersError && !profileError) {
      setAlert(t('common.saved'), 'success');
      router.navigate('/students');
    }
  }, [setAlert, router, userError, usersError, profileError, t]);

  const onSubmit = useCallback(
    async (dataProfile: any) => {
      const profile = { ...dataProfile };

      try {
        if (profile.id) {
          // mise en parallèle des mises à jour user et profil
          const updatePromises = [];

          // Ajouter la mise à jour du profil
          updatePromises.push(updateProfile(profile.id, profile));

          // Vérifier si les données utilisateur modifiables ont changé
          if (
            profile.user.email !== user.email ||
            profile.user.blocked !== user.blocked ||
            profile.user.confirmed !== user.confirmed
          ) {
            // Ajouter la mise à jour de l'utilisateur
            updatePromises.push(
              updateUser(id, {
                ...user,
                email: profile.user.email,
                blocked: profile.user.blocked,
                confirmed: profile.user.confirmed,
              })
            );
          }

          // Exécuter les requêtes simultanément
          await Promise.all(updatePromises);
        } else {
          await saveProfile({ ...profile, user: userId });
        }
        // refresh item
        await refreshUsers();
      } catch (e: any) {
        return e;
      }

      back();
      return true;
    },
    [
      updateProfile,
      saveProfile,
      updateUser,
      refreshUsers,
      back,
      user,
      id,
      userId,
      userError,
      usersError,
      profileError,
      t,
    ]
  );

  return isFetching || !user ? null : <StudentForm onSubmit={onSubmit} editedUser={user} />;
}

export default Student;
